import type { PropsWithChildren } from "react";
import { createContext, memo, useContext, useMemo } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useFeatureFlags } from "@jti/commerce";
import { isAddressFormExtendedWithProvince } from "@jti/fe-common";
import { useGetStoreConfigQuery } from "@jti/store/slices/magentoApiSlice";
import { useGetProfileQuery } from "@jti/store/slices/profileApiSlice";
import { getProvinceCodeTranslationKey, useTranslations } from "@jti/ui";

import { addressCustomShippingFieldFactory } from "@checkout-v2/formFields/factories/addressCustomShippingFieldFactory";
import { addressHomeDeliveryFieldFactory } from "@checkout-v2/formFields/factories/addressHomeDeliveryFieldFactory";
import { addressInStoreDeliveryFieldFactory } from "@checkout-v2/formFields/factories/addressInStoreDeliveryFactory";
import { billingAddressFieldFactory } from "@checkout-v2/formFields/factories/billingAddressFieldFactory";
import { deliveryOptionsFactory } from "@checkout-v2/formFields/factories/deliveryOptionsFactory";
import { firstNameFieldFactory } from "@checkout-v2/formFields/factories/firstNameFieldFactory/firstNameFieldFactory";
import { isBillingAddressSameAsShippingFieldFactory } from "@checkout-v2/formFields/factories/isBillingAddressSameAsShippingFieldFactory";
import { lastNameFieldFactory } from "@checkout-v2/formFields/factories/lastNameFieldFactory/lastNameFieldFactory";
import { shippingMethodFieldFactory } from "@checkout-v2/formFields/factories/shippingMethodCodeFieldFactory";
import type { ShouldUseProfileDataFieldConfigArgs } from "@checkout-v2/formFields/factories/shouldUseProfileDataFieldFactory/shouldUseProfileDataFieldFactory";
import { shouldUseProfileDataFieldFactory } from "@checkout-v2/formFields/factories/shouldUseProfileDataFieldFactory/shouldUseProfileDataFieldFactory";
import type { TelephoneFieldGetDefaultValueArgs } from "@checkout-v2/formFields/factories/telephoneFieldFactory/telephoneFieldFactory";
import { telephoneFieldFactory } from "@checkout-v2/formFields/factories/telephoneFieldFactory/telephoneFieldFactory";
import type { ShippingFormFieldsConfig, ShippingFormFieldsDefaultValues } from "@checkout-v2/forms";
import { useCountry } from "@checkout-v2/shared/hooks/useCountry";
import { useDeliveryOptions } from "@checkout-v2/shared/hooks/useDeliveryOptions";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider/hooks/";

import type { ShippingFormDataContextValue } from "./ShippingFormDataProvider.types";

const ShippingFormDataContext = createContext<ShippingFormDataContextValue | null>(null);

export const useShippingFormDataContext = (): ShippingFormDataContextValue => {
  const context = useContext(ShippingFormDataContext);

  if (!context) {
    throw new Error("useShippingFormDataContext must be used within ShippingFormDataProvider");
  }

  return context;
};

export const ShippingFormDataProvider = memo(({ children }: PropsWithChildren) => {
  const { __ } = useTranslations();
  const config = useConfig();
  const featureFlags = useFeatureFlags();

  const profileQueryStatus = useGetProfileQuery();
  const storeConfigQueryState = useGetStoreConfigQuery();
  const countryQueryState = useCountry();
  const checkoutConfiguration = useCheckoutV2Configuration();
  const isShippingAgeVerificationEnabled =
    featureFlags.ffCheckoutV2EnableIteration2 && storeConfigQueryState.data?.ageVerificationMode === "SHIPPING";

  const {
    cartId,
    deliveryOptionsData,
    allDeliveryOptions,
    setShippingMethodOnCart,
    isLoading: isDeliveryOptionsLoading,
  } = useDeliveryOptions();

  const telephoneArgs: TelephoneFieldGetDefaultValueArgs | null = useMemo(() => {
    if (!checkoutConfiguration || profileQueryStatus.status !== "fulfilled") {
      return null;
    }

    return {
      checkoutConfig: checkoutConfiguration,
      config,
      profile: profileQueryStatus.data!,
    };
  }, [checkoutConfiguration, config, profileQueryStatus.data, profileQueryStatus.status]);

  const useProfileDataArgs: ShouldUseProfileDataFieldConfigArgs = useMemo(
    () => ({
      checkoutConfig: checkoutConfiguration,
      __,
      featureFlags,
    }),
    [__, checkoutConfiguration, featureFlags]
  );

  const formFieldsConfig: ShippingFormFieldsConfig | null = useMemo(() => {
    if (
      !telephoneArgs ||
      storeConfigQueryState.status !== "fulfilled" ||
      profileQueryStatus.status !== "fulfilled" ||
      !countryQueryState.data
    ) {
      return null;
    }

    return {
      shouldUseProfileData: shouldUseProfileDataFieldFactory.getDerivedConfig(useProfileDataArgs),
      telephone: telephoneFieldFactory.getDerivedConfig(telephoneArgs),
      firstName: firstNameFieldFactory.getDerivedConfig({
        __,
        storeConfig: storeConfigQueryState.data!,
        profile: profileQueryStatus.data!,
      }),
      lastName: lastNameFieldFactory.getDerivedConfig({
        __,
        storeConfig: storeConfigQueryState.data!,
        profile: profileQueryStatus.data!,
      }),
      shippingMethodCode: shippingMethodFieldFactory.getDerivedConfig(checkoutConfiguration.shippingMethods),
      addressHomeDelivery: addressHomeDeliveryFieldFactory.getDerivedConfig({
        countryName: config.COUNTRY_NAME,
        countryCode: config.ISO_COUNTRY_CODE,
        userProfileAddresses: profileQueryStatus.data?.addresses ?? [],
        ...(isAddressFormExtendedWithProvince(config.addressFormVariant) &&
          profileQueryStatus.data?.extra?.province && {
            provinceNameOverride: __(
              getProvinceCodeTranslationKey(profileQueryStatus.data.extra.province),
              [],
              profileQueryStatus.data.extra.province
            ),
          }),
      }),
      addressInStoreDelivery: addressInStoreDeliveryFieldFactory.getDerivedConfig(checkoutConfiguration, __),
      addressCustomShipping: addressCustomShippingFieldFactory.getDerivedConfig(checkoutConfiguration, __),
      isBillingAddressSameAsShipping: isBillingAddressSameAsShippingFieldFactory.getDerivedConfig(__),
      billingAddress: billingAddressFieldFactory.getDerivedConfig(
        config.isBillingAddressVisibleForCheckout,
        profileQueryStatus.data?.addresses ?? [],
        config.COUNTRY_NAME,
        config.ISO_COUNTRY_CODE
      ),
      deliveryOption: deliveryOptionsFactory.getDerivedConfig({
        options: allDeliveryOptions,
        selectedOption: deliveryOptionsData?.methodSelected,
        cartId: cartId,
        onChange: setShippingMethodOnCart,
        isLoading: isDeliveryOptionsLoading,
        __: __,
      }),
    };
    // TODO - fix deps arrays values after exhaustive-deps as error - DESP-111178
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    __,
    checkoutConfiguration.shippingMethods,
    storeConfigQueryState.data,
    storeConfigQueryState.status,
    profileQueryStatus.data,
    profileQueryStatus.status,
    telephoneArgs,
    useProfileDataArgs,
    allDeliveryOptions,
    deliveryOptionsData?.methodSelected,
    isDeliveryOptionsLoading,
  ]);

  const initialDefaultValues: ShippingFormFieldsDefaultValues | null = useMemo(() => {
    if (
      !telephoneArgs ||
      profileQueryStatus.status !== "fulfilled" ||
      storeConfigQueryState.status !== "fulfilled" ||
      !formFieldsConfig?.addressHomeDelivery
    ) {
      return null;
    }

    const shippingMethodCodeDefaultValue = shippingMethodFieldFactory.getDefaultValue(
      checkoutConfiguration.shippingMethods,
      {
        isShippingAgeVerificationEnabled,
        isAgeVerified: profileQueryStatus.data?.is_age_verified === true,
      }
    );
    const isAtHomeDelivery =
      shippingMethodFieldFactory.getSelectedShippingMethodType(
        shippingMethodCodeDefaultValue,
        checkoutConfiguration
      ) === "atHome";

    const billingAddressDefaultValue = billingAddressFieldFactory.getDefaultValue({
      isBillingAddressVisibleForCheckout: config.isBillingAddressVisibleForCheckout,
      userProfileAddresses: profileQueryStatus.data?.addresses ?? [],
    });

    const isBillingAddressSameAsShipping = isBillingAddressSameAsShippingFieldFactory.getDefaultValue({
      isAtHomeDelivery,
    });

    const defaultBillingAddress =
      billingAddressDefaultValue ??
      (config.isBillingAddressVisibleForCheckout ? formFieldsConfig.billingAddress.emptyAddress : null);
    const billingAddress =
      featureFlags.ffCheckoutV2EnableIteration2 && isBillingAddressSameAsShipping ? null : defaultBillingAddress;

    return {
      shouldUseProfileData: shouldUseProfileDataFieldFactory.getDefaultValue(),
      telephone: telephoneFieldFactory.getDefaultValue(telephoneArgs),
      telephoneFallback: formFieldsConfig.telephone.savedDefaultValue,
      firstName: firstNameFieldFactory.getDefaultValue({
        profile: profileQueryStatus.data!,
        storeConfig: storeConfigQueryState.data!,
      }),
      lastName: lastNameFieldFactory.getDefaultValue({ profile: profileQueryStatus.data! }),
      shippingMethodCode: shippingMethodCodeDefaultValue,
      addressHomeDelivery: addressHomeDeliveryFieldFactory.getDefaultValue({
        fieldConfig: formFieldsConfig.addressHomeDelivery,
      }),
      addressInStoreDelivery: addressInStoreDeliveryFieldFactory.getDefaultValue(),
      addressCustomShipping: addressCustomShippingFieldFactory.getDefaultValue(),
      isBillingAddressSameAsShipping,
      billingAddress,
      deliveryOption: deliveryOptionsFactory.getDefaultValue({
        options: allDeliveryOptions,
        selectedOption: deliveryOptionsData?.methodSelected,
        __: __,
      }),
      ...(isAddressFormExtendedWithProvince(config.addressFormVariant) && {
        _hiddenValues: { immutableProvinceCode: profileQueryStatus.data?.extra?.province ?? "" },
      }),
    };
    // TODO - fix deps arrays values after exhaustive-deps as error - DESP-111178
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    telephoneArgs,
    profileQueryStatus.status,
    profileQueryStatus.data,
    storeConfigQueryState.status,
    storeConfigQueryState.data,
    checkoutConfiguration.shippingMethods,
    formFieldsConfig?.addressHomeDelivery,
    allDeliveryOptions,
    deliveryOptionsData?.methodSelected,
    featureFlags.ffCheckoutV2EnableIteration2,
  ]);

  const value = useMemo(
    () => ({
      initialDefaultValues,
      formFieldsConfig,
      isShippingAgeVerificationEnabled,
      isLoading: isDeliveryOptionsLoading,
    }),
    [initialDefaultValues, formFieldsConfig, isShippingAgeVerificationEnabled, isDeliveryOptionsLoading]
  );

  return <ShippingFormDataContext.Provider value={value}>{children}</ShippingFormDataContext.Provider>;
});

ShippingFormDataProvider.displayName = "ShippingFormDataProvider";
